<template>
  <div class="row align-items-center mt-2">
    <h1 class="my-auto">Table analysis</h1>

    <StatusComponent class="mr-2 ml-3 my-auto" :status="tableStatus">
    </StatusComponent>

    <div class="col"></div>

    <a href="#" class="my-auto mb-4 mt-3 ml-2 btn btn-sm btn-secondary" v-on:click="onRefreshTable">
      <span class="mr-2 d-none d-md-inline-block">Refresh</span>
      <span class="icon icon-reload mr-0"></span>
    </a>
    <a v-if="showLoadingButton" href="#" class="my-auto mb-4 mt-3 ml-2 btn btn-sm btn-secondary disabled">
      <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
      Loading...
    </a>
    <div v-if="!showLoadingButton" class="position-relative" id="dropdownBoundaryContainer">

      <a id="tableAnalysisActionLauncher"  class="position-relative my-auto mb-4 mt-3 ml-2 btn btn-sm btn-secondary" data-boundary="dropdownBoundaryContainer" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" :class="{'disabled': isInProgress}">
        <span class="mr-2 d-none d-md-inline-block">Start Actions</span>
        <span class="icon icon-More-dots mr-0" :style="{'transform': 'rotate(90deg)'}"></span>
      </a>

      <ul class="dropdown-menu dropdown-menu-right mt-0" role="menu" aria-labelledby="tableAnalysisActionLauncher">
        <div class="px-1 py-1">
          <div :title="launchOrRestartPreprocessingForbidden ? launchOrRestartPreprocessingForbiddenCause : launchOrRestartPreprocessingButtonText">
            <li href="#" class="dropdown-item btn btn-sm" v-on:click="onStartTablePreProcessing()" :active="false" :class="{'disabled': launchOrRestartPreprocessingForbidden }">{{ launchOrRestartPreprocessingButtonText }}</li>
          </div>
          <div :title="launchOrRestartAnnotationForbidden ? launchOrRestartAnnotationForbiddenCause : launchOrRestartAnnotationButtonText">
            <li href="#" class="dropdown-item btn btn-sm" v-on:click="onStartTableAnnotation()" :active="false" :class="{'disabled': launchOrRestartAnnotationForbidden }">{{ launchOrRestartAnnotationButtonText }}</li>
          </div>
          <MailtoLink linkText="Contact support" :emailContentObjects="{preprocessingResult: PreProcessingResult, annotationContent: AnnotationContent}">
            <li class="dropdown-item btn btn-sm">Contact support</li>
          </MailtoLink>
        </div>
      </ul>

    </div>

    <div class="col-12 mb-3"></div>

    <div v-if="(isInProgress === false) && (PreProcessingResult !== undefined)" class="col-12">
      <div class="col"></div>
      <div class="col">
        <div id="accordionPreProcessingAnalysis" role="tablist" class="o-nav-local mb-4">
          <nav class="nav flex-column flex-md-row">
            <a class="nav-link" :class="{'active': isActiveDisplay('Preprocessing Result')}" v-on:click="selectContentToDisplay('Preprocessing Result')">Preprocessing Result</a>
            <a v-if="SHOW_PREPROCESS_TABLES_DATA" class="nav-link" :class="{'active': isActiveDisplay('Preprocessed Tables Data')}" v-on:click="selectContentToDisplay('Preprocessed Tables Data')">Preprocessed Tables Data</a>
            <a class="nav-link" :class="{'active': isActiveDisplay('Tables Data Comparison')}" v-on:click="selectContentToDisplay('Tables Data Comparison')">Preprocessed Tables Data Comparison</a>
            <a v-if="isAnnotationContentDefined" class="nav-link" :class="{'active': isActiveDisplay('Semantic Annotations')}" v-on:click="selectContentToDisplay('Semantic Annotations')">Semantic Annotations</a>
            <a v-if="isAnnotationContentDefined" class="nav-link" :class="{'active': isActiveDisplay('Annotation Knowledge Graph')}" v-on:click="selectContentToDisplay('Annotation Knowledge Graph')">Annotation Knowledge Graph</a>
          </nav>
        </div>
      </div>
    </div>

    <ProgressBar class="mb-5"
      v-bind:invisible="isInProgress === false"
      v-bind:striped="true"
      v-bind:animated="true"
      v-bind:animatedIndeterminate="true"
      :class="{'d-none': (isInProgress === false)}">
    </ProgressBar>

    <div v-if="(isInProgress === false) && isActiveDisplay('Preprocessing Result')" id="PreProcessingResultContent" class="row col-12">
      <div class="col"></div>
      <PreProcessingResult class="" :PreProcessingResult="PreProcessingResult"></PreProcessingResult>
      <div class="col"></div>
    </div>

    <div v-if="SHOW_PREPROCESS_TABLES_DATA && isPreProcessingResultDefined && isActiveDisplay('Preprocessed Tables Data')" id="PreProcessedTablesDataContent" class="row col-12">
      <div class="col"></div>
      <TableContentDisplayer class="" :orientation="PreProcessingResultOrientation" :hasHeader="PreProcessingResultHasHeader" :headerPosition="PreProcessingResultHeaderPosition" :tableContentDisplayerTitle="'Input Table Data'" :tableMatrixToDisplay="PreProcessingResultTableDataRawMatrix" :repeatHeaderInArrayContent="false"></TableContentDisplayer>
      <div class="col"></div>
      <div class="col-12"></div>
      <div class="col"></div>
      <TableContentDisplayer class="" :orientation="PreProcessingResultOrientation" :hasHeader="PreProcessingResultHasHeader" :headerPosition="PreProcessingResultHeaderPosition" :tableContentDisplayerTitle="'Revised Table Data'" :tableMatrixToDisplay="PreProcessingResultTableDataRevisedMatrix" :repeatHeaderInArrayContent="false"></TableContentDisplayer>
      <div class="col"></div>
    </div>

    <div v-if="isPreProcessingResultDefined && isActiveDisplay('Tables Data Comparison')" id="PreProcessedTablesDataComparison" class="row col-12">
      <div class="col"></div>
      <TableContentAnalyzer class="" :orientation="PreProcessingResultOrientation" :hasHeader="PreProcessingResultHasHeader" :headerPosition="PreProcessingResultHeaderPosition" :tableContentAnalyzerTitle="'Tables Data comparison'" :originalTableMatrixToCompare="PreProcessingResultTableDataRawMatrix" :resultTableMatrixToCompare="PreProcessingResultTableDataRevisedMatrix"></TableContentAnalyzer>
      <div class="col"></div>
    </div>

    <div v-if="isAnnotationContentDefined && isPreProcessingResultDefined && isActiveDisplay('Semantic Annotations')" id="AnnotationTablesContent" class="row col-12">
      <div class="col"></div>
      <AnnotationResultDisplayer :PreProcessingResult="PreProcessingResult"
                         :AnnotationContent="AnnotationContent"
                         :tableId="tableId"
                         :tableStatus="tableStatus"
                         :isInProgress="isInProgress"></AnnotationResultDisplayer>
      <div class="col"></div>
    </div>

    <div v-if="isAnnotationContentDefined && isPreProcessingResultDefined && isActiveDisplay('Annotation Knowledge Graph')" id="AnnotationKnowledgeGraph" class="row col-12">
      <KnowledgeGraph :PreProcessingResult="PreProcessingResult"
                      :AnnotationContent="AnnotationContent"></KnowledgeGraph>
    </div>

  </div>

</template>
<script>
import MailtoLink from "../../global/MailtoLink.vue";
import AnnotationResultDisplayer from "./AnnotationResultDisplayer";
import TableContentAnalyzer from "./TableContentAnalyzer";
import TableContentDisplayer from "./TableContentDisplayer";
import StatusComponent from "./StatusComponent";
// import ScoredDataComponent from "./ScoredDataComponent";
import PreProcessingResult from "./PreProcessingResult";
import TableMatrix from '../../../model/TableMatrix';
import ProgressBar from '@/components/global/ProgressBar.vue'
import KnowledgeGraph from "../../d3/KnowLedgeGraph";

export default {
  name: "TableAnalysisContainer",
  components: {
    MailtoLink,
    KnowledgeGraph,
//    ScoredDataComponent,
    AnnotationResultDisplayer,
    StatusComponent,
    TableContentAnalyzer,
    TableContentDisplayer,
    PreProcessingResult,
    ProgressBar
  },
  props: ["PreProcessingResult", "AnnotationContent", "tableId", "tableStatus", "isInProgress", "isTableFileStillExisting"],
  data() {
    return {
      contentToDisplay: 'Preprocessing Result',
      SHOW_PREPROCESS_TABLES_DATA: false
    }
  },
  mounted() {
  },
  computed: {
    showLoadingButton: function() {
      return (this.tableStatus === undefined) || ( (this.isInProgress === false) && !this.isPreProcessingResultDefined && ['PRE PROCESSING IN PROGRESS', 'PRE PROCESSING DONE'].includes(this.tableStatus) );
    },
    launchOrRestartPreprocessingButtonText: function() {
      if (['UPLOADED', 'PRE PROCESSING IN PROGRESS'].includes(this.tableStatus) && (this.PreProcessingResult === undefined)) {
        return 'Start PreProcessing';
      } else {
        return 'Restart PreProcessing';
      }
    },
    launchOrRestartAnnotationButtonText: function() {
      if (['UPLOADED', 'PRE PROCESSING IN PROGRESS', 'PRE PROCESSING IN ERROR', 'PRE PROCESSING FAILED', 'PRE PROCESSING DONE', 'ANNOTATION IN PROGRESS'].includes(this.tableStatus) && (this.AnnotationContent === undefined)) {
        return 'Start Annotation';
      } else {
        return 'Restart Annotation';
      }
    },
    launchOrRestartPreprocessingForbidden: function() {
      return !this.isTableFileStillExisting;
    },
    launchOrRestartPreprocessingForbiddenCause: function() {
      return 'Forbidden: uploaded file is missing';
    },
    launchOrRestartAnnotationForbidden: function() {
      return ['UPLOADED', 'PRE PROCESSING IN ERROR', 'PRE PROCESSING FAILED', 'PRE PROCESSING IN PROGRESS'].includes(this.tableStatus) && !this.isTableFileStillExisting;
    },
    launchOrRestartAnnotationForbiddenCause: function() {
      return 'Forbidden: uploaded file is missing';
    },
    isPreProcessingResultDefined: function() {
      return (this.PreProcessingResult !== undefined) && (Array.isArray(this.PreProcessingResult)) && (this.PreProcessingResult[0] !== undefined);
    },
    PreProcessingResultOrientation: function() {
      return this.isPreProcessingResultDefined ? this.PreProcessingResult[0].preprocessed.tableOrientation.orientationLabel : undefined;
    },
    PreProcessingResultHasHeader: function() {
      return this.isPreProcessingResultDefined ? this.PreProcessingResult[0].preprocessed.headerInfo.hasHeader : undefined;
    },
    PreProcessingResultHeaderPosition: function() {
      return this.isPreProcessingResultDefined ? this.PreProcessingResult[0].preprocessed.headerInfo.headerPosition : undefined;
    },
    PreProcessingResultTableDataRawMatrix: function() {
      return this.isPreProcessingResultDefined ? new TableMatrix(this.PreProcessingResult[0].raw.tableDataRaw) : new TableMatrix(undefined);
    },
    PreProcessingResultTableDataRevisedMatrix: function() {
      return this.isPreProcessingResultDefined ? new TableMatrix(this.PreProcessingResult[0].preprocessed.tableDataRevised) : new TableMatrix(undefined);
    },
    signedUser: function() {
      return (this.$store.state.identityIdentified !== undefined) ? this.$store.state.identityIdentified.email : undefined;
    },
    selectedProjectId: function() {
      return (this.$store.state.selectedProject !== undefined) ? this.$store.state.selectedProject.id : undefined;
    },
    isAnnotationContentDefined: function() {
      return ((this.AnnotationContent !== undefined) && (this.AnnotationContent[0] !== undefined));
    },
    displayedAnnotationContent: function() {
      if (this.isAnnotationContentDefined()) {
        return this.AnnotationContent[0];
      } else {
        return undefined;
      }
    },
  },
  methods: {
    selectContentToDisplay: function(displayName) {
      if (displayName === 'Preprocessing Result'){
        this.contentToDisplay = displayName;
      } else if (displayName === 'Preprocessed Tables Data'){
        this.contentToDisplay = displayName;
      } else if (displayName === 'Tables Data Comparison'){
        this.contentToDisplay = displayName;
      } else if (displayName === 'Semantic Annotations'){
        this.contentToDisplay = displayName;
      } else if (displayName === 'Annotation Knowledge Graph'){
        this.contentToDisplay = displayName;
      } else {
        console.log("[selectContentToDisplay] unknown display = ", displayName);
      }
    },
    isActiveDisplay: function(displayName) {
      return displayName === this.contentToDisplay;
    },
    onStartTablePreProcessing: function() {
      console.log("[onStartTablePreProcessing] -~~-> start-table-preprocessing");
      this.$emit('start-table-preprocessing');
    },
    onRefreshTable: function() {
      console.log("[onRefreshTable] -~~-> refresh-table");
      this.$emit('refresh-table');
    },
    onStartTableAnnotation: function() {
      console.log("[onStartTableAnnotation] -~~-> start-table-annotation");
      this.$emit('start-table-annotation');
    }
  }

};
</script>
<style scoped>

.scrolling-wrapper{
  overflow-x: auto;
  white-space: nowrap;
  /* flex-wrap: nowrap; */
}

.dropdown-menu{
  max-heght: 100px;
  overflow-y: auto
}

#accordionPreProcessingAnalysis.o-nav-local .nav-link {
  padding: 0.775rem 1rem;
}

</style>